import { useState, useMemo, useCallback } from 'react';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { GetAssureDetailsResponse } from 'api/actor';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

export enum PersonTabsEnum {
  GENERAL_INFO = 'general_info',
  PARC = 'parc',
  CONTRACTS = 'contracts',
  QUOTES = 'quotes',
  SIMULATIONS = 'simulations',
  NOTES = 'notes',
  HISTORY = 'history',
  // Actions
  NEW_NOTE = 'new_note',
  SEND_MAIL = 'send_mail'
}

export type TabActionType = 'navigate' | 'action' | 'modal' | 'external';

export interface TabAction {
  type: TabActionType;
  handler?: () => void | Promise<void>;
  modalConfig?: {
    title: string;
    component: React.ComponentType<any>;
    props?: any;
  };
  externalUrl?: string;
}

export interface TabConfig {
  value: PersonTabsEnum;
  label: string;
  icon: string;
  description?: string;
  badge?: string | number;
  disabled?: boolean;
  category?: 'main' | 'actions';
  action?: TabAction;
  isAction?: boolean;
}

interface UsePersonContentTabsProps {
  personDetails?: GetAssureDetailsResponse;
  /** Nombre de biens du parc immobilier — l'onglet Parc est masqué à 0. */
  parcCount?: number;
  initialTab?: PersonTabsEnum;
  onNewNote?: () => void;
  onSendMail?: () => void;
  onOpenModal?: (modalConfig: any) => void;
  onShowNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
}

export const usePersonContentTabs = ({
  personDetails,
  parcCount = 0,
  initialTab = PersonTabsEnum.GENERAL_INFO,
  onNewNote,
  onSendMail,
  onOpenModal,
  onShowNotification
}: UsePersonContentTabsProps) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const [activeTab, setActiveTab] = useState<PersonTabsEnum>(initialTab);
  // Fiche client (matrice centrale) : notes + actions rapides réservées au Courtier.
  const canNotes = useRoleCan(Capability.NotesAccess);
  const canClientActions = useRoleCan(Capability.ClientDetailActions);

  // Actions personnalisées
  const handleNewNoteAction = useCallback(async () => {
    setActiveTab(PersonTabsEnum.NOTES);
    onNewNote?.();
  }, [onNewNote]);

  const handleSendMailAction = useCallback(async () => {
    onSendMail?.();
  }, [onSendMail]);

  // Configuration des onglets
  const tabsConfig = useMemo((): TabConfig[] => ([
    {
      value: PersonTabsEnum.GENERAL_INFO,
      label: 'Informations générales',
      icon: 'user',
      description: 'Informations générales de la personne',
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.PARC,
      label: 'Biens immobiliers',
      icon: 'home',
      description: 'Biens et parcs immobiliers du client',
      badge: parcCount,
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.CONTRACTS,
      label: 'Contrats',
      icon: 'document',
      description: 'Contrats associés',
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.QUOTES,
      label: 'Devis',
      icon: 'receiptText',
      description: 'Devis en cours',
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.SIMULATIONS,
      label: 'Simulations',
      icon: 'calculator',
      description: 'Simulations effectuées',
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.NOTES,
      label: 'Notes',
      icon: 'noteSticky',
      description: 'Notes et commentaires',
      category: 'main',
      isAction: false
    },
    {
      value: PersonTabsEnum.HISTORY,
      label: 'Historique',
      icon: 'history',
      description: 'Historique des modifications',
      category: 'main',
      isAction: false,
      disabled: true
    },
    // ACTIONS
    {
      value: PersonTabsEnum.NEW_NOTE,
      label: 'Nouvelle note',
      icon: 'pencil',
      description: 'Ajouter une nouvelle note',
      category: 'actions',
      isAction: true,
      action: {
        type: 'action',
        handler: handleNewNoteAction
      }
    },
    {
      value: PersonTabsEnum.SEND_MAIL,
      label: t('COMMON.SEND_MAIL'),
      icon: 'send',
      description: 'Envoyer un email',
      category: 'actions',
      isAction: true,
      action: {
        type: 'action',
        handler: handleSendMailAction
      }
    }
  ] as TabConfig[]).filter((tab) => {
    // Actions rapides (mail, note…) : Courtier uniquement.
    if (!canClientActions && tab.category === 'actions') return false;
    // Notes hors périmètre (consultation + création) pour les rôles sans droit notes.
    if (!canNotes && (tab.value === PersonTabsEnum.NOTES || tab.value === PersonTabsEnum.NEW_NOTE)) return false;
    // Parc immobilier : onglet masqué tant que le client n'a aucun bien.
    if (tab.value === PersonTabsEnum.PARC && parcCount === 0) return false;
    return true;
  }), [t, handleNewNoteAction, handleSendMailAction, canNotes, canClientActions, parcCount]);

  // Onglets groupés par catégorie
  const groupedTabs = useMemo(() => {
    const groups = tabsConfig.reduce((acc, tab) => {
      const category = tab.category || 'main';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tab);
      return acc;
    }, {} as Record<string, TabConfig[]>);

    return groups;
  }, [tabsConfig]);

  // Gestion du changement d'onglet
  const handleTabChange = useCallback(async (value: PersonTabsEnum) => {
    const tab = tabsConfig.find(t => t.value === value);

    if (!tab || tab.disabled) {
      return;
    }

    if (tab.isAction && tab.action) {
      try {
        switch (tab.action.type) {
          case 'action':
            if (tab.action.handler) {
              await tab.action.handler();
            }
            break;

          case 'modal':
            if (tab.action.modalConfig && onOpenModal) {
              onOpenModal(tab.action.modalConfig);
            }
            break;

          case 'external':
            if (tab.action.externalUrl) {
              window.open(tab.action.externalUrl, '_blank');
            }
            break;

          default:
            console.warn(`Type d'action non géré: ${tab.action.type}`);
        }
      } catch (error) {
        console.error('Erreur lors de l\'exécution de l\'action:', error);
        onShowNotification?.('Erreur lors de l\'exécution de l\'action', 'error');
      }
    } else {
      setActiveTab(value);
    }
  }, [tabsConfig, onOpenModal, onShowNotification]);

  const getActiveTab = useCallback(() => {
    return tabsConfig.find(tab => tab.value === activeTab) || tabsConfig[0];
  }, [activeTab, tabsConfig]);

  const getTabsByCategory = useCallback((category: string) => {
    return groupedTabs[category] || [];
  }, [groupedTabs]);

  const isTabActive = useCallback((tabValue: PersonTabsEnum) => {
    return activeTab === tabValue;
  }, [activeTab]);

  const isTabDisabled = useCallback((tabValue: PersonTabsEnum) => {
    const tab = tabsConfig.find(t => t.value === tabValue);
    return tab?.disabled || false;
  }, [tabsConfig]);

  const isTabAction = useCallback((tabValue: PersonTabsEnum) => {
    const tab = tabsConfig.find(t => t.value === tabValue);
    return tab?.isAction || false;
  }, [tabsConfig]);

  return {
    activeTab,
    tabsConfig,
    groupedTabs,
    handleTabChange,
    setActiveTab,
    getActiveTab,
    getTabsByCategory,
    isTabActive,
    isTabDisabled,
    isTabAction
  };
};
